import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import API_URL from '../config';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


const DailySuccessJournal = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [pastDates, setPastDates] = useState([]);
  const [showingPastEntries, setShowingPastEntries] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    wins: ['', '', ''],
    gratitudes: ['', '', ''],
    well: '',
    better: '',
    how: '',
    notes: ''
  });

  useEffect(() => {
    // Track component view
    Mixpanel.trackFeatureAction('Journal', 'View Daily Journal');
    
    // Check if there's a journal entry for today
    checkTodayEntry();
  }, []);

  // Check if there's a journal entry for today
  const checkTodayEntry = async (specificDate = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const date = specificDate || new Date().toISOString().split('T')[0];
      try {
        // Make sure we use the correct API endpoint with /api prefix
        const response = await axios.get(`${API_URL}/api/journal`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            date, 
            type: 'daily' 
          }
        });
        
        if (response.data && response.data.entry) {
          // Map backend data structure to frontend structure
          const entry = response.data.entry;
          setFormData({
            date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : date,
            wins: Array.isArray(entry.wins) && entry.wins.length === 3 ? entry.wins : ['', '', ''],
            gratitudes: Array.isArray(entry.gratitudes) && entry.gratitudes.length === 3 ? entry.gratitudes : ['', '', ''],
            well: entry.well || '',
            better: entry.better || '',
            how: entry.how || '',
            notes: entry.notes || ''
          });
        }
      } catch (error) {
        // If it's a 404, that just means no entry exists yet, which is fine
        if (error.response && error.response.status === 404) {
          logger.debug('No journal entry found for today, creating a new one');
          // Reset form data for a new entry
          setFormData({
            date: date,
            wins: ['', '', ''],
            gratitudes: ['', '', ''],
            well: '',
            better: '',
            how: '',
            notes: ''
          });
        } else {
          // For other errors, log them but don't show to user
          logger.error('Error fetching today\'s journal entry:', error);
        }
      }
    } catch (error) {
      logger.error('Unexpected error in checkTodayEntry:', error);
    }
  };

  // Fetch all past journal entries
  const fetchPastJournalEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Get all journal dates for the daily type
      // Use the correct endpoint for journal dates with /api prefix
      const response = await axios.get(`${API_URL}/api/journal/dates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { type: 'daily' }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Sort dates in descending order (newest first)
        const sortedDates = response.data.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setPastDates(sortedDates);
        setShowingPastEntries(true);
      }
    } catch (error) {
      logger.error('Error fetching past journal entries:', error);
      setError('Failed to load past journal entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If date is changing, reset the form and check for entries on the new date
    if (name === 'date' && value !== formData.date) {
      setFormData({
        date: value,
        wins: ['', '', ''],
        gratitudes: ['', '', ''],
        well: '',
        better: '',
        how: '',
        notes: ''
      });
      
      // Check if there's an entry for the new date
      setTimeout(() => {
        checkTodayEntry(value);
      }, 100);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    setSaved(false);
  };

  const handleArrayInputChange = (index, field, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return {
        ...prev,
        [field]: newArray
      };
    });
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSaved(false);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save a journal entry');
        return;
      }
      
      // Filter out empty strings from arrays and prepare data for backend
      const backendData = {
        date: formData.date,
        wins: formData.wins.map(win => win.trim()).filter(Boolean),
        gratitudes: formData.gratitudes.map(gratitude => gratitude.trim()).filter(Boolean),
        well: formData.well,
        better: formData.better,
        how: formData.how,
        notes: formData.notes,
        type: 'daily'
      };
      
      await axios.post(`${API_URL}/api/journal`, backendData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSaved(true);
      
      // Mark first journal entry task as completed
      markJournalTaskCompleted();
      
      // Track successful journal entry
      Mixpanel.trackFeatureAction('Journal', 'Daily Journal Saved', {
        date: formData.date
      });
      
    } catch (error) {
      logger.error('Error saving journal entry:', error);
      setError('Failed to save your journal entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const markJournalTaskCompleted = async () => {
    // Set localStorage flag
    localStorage.setItem('firstJournalEntry', 'true');
    
    // Update the server if possible
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Log the request payload for debugging
        console.log('Sending starter task update:', { taskId: 'firstJournalEntry', completed: true });
        
        const response = await axios.post(
          `${API_URL}/api/users/starter-task`, 
          { taskId: 'firstJournalEntry', completed: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('Starter task update response:', response.data);
      } catch (error) {
        logger.error('Error marking journal task completed:', error);
        logger.debug('Error details:', error.response?.data || 'No response data');
        // Still use local storage as fallback
        localStorage.setItem('firstJournalEntry', 'true');
      }
    }
  };
  
  const handleNewEntry = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      wins: ['', '', ''],
      gratitudes: ['', '', ''],
      well: '',
      better: '',
      how: '',
      notes: ''
    });
    setSaved(false);
    setShowingPastEntries(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Daily Success Journal</h2>
      
      {error && (
        <div className="text-center mb-4">
          <p className="text-red-500 font-medium">{error}</p>
        </div>
      )}
      
      {saved ? (
        <div className="text-center py-8">
          <div className="alert alert-success mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Your journal entry has been saved successfully!</span>
          </div>
          <button 
            onClick={handleNewEntry}
            className="btn btn-primary"
          >
            Create New Entry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={fetchPastJournalEntries}
              className="btn btn-secondary btn-sm"
            >
              {showingPastEntries ? "Hide Past Entries" : "Review Past Journals"}
            </button>
            
            {showingPastEntries && (
              <button
                type="button"
                onClick={handleNewEntry}
                className="btn btn-outline btn-sm"
              >
                New Entry
              </button>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
              Date
            </label>
            {showingPastEntries ? (
              <select
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                required
              >
                <option value="" disabled>Select a date</option>
                {pastDates.map(entry => (
                  <option key={entry._id || entry.date} value={entry.date}>
                    {new Date(entry.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                required
              />
            )}
          </div>
          
          {/* 3 Wins Today */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              3 Wins Today
            </label>
            {[0, 1, 2].map((index) => (
              <div key={`win-${index}`} className="mb-2">
                <input
                  type="text"
                  value={formData.wins[index]}
                  onChange={(e) => handleArrayInputChange(index, 'wins', e.target.value)}
                  className="input input-bordered w-full"
                  placeholder={`Win #${index + 1}`}
                />
              </div>
            ))}
          </div>
          
          {/* 3 Gratitudes */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              3 Gratitudes
            </label>
            {[0, 1, 2].map((index) => (
              <div key={`gratitude-${index}`} className="mb-2">
                <input
                  type="text"
                  value={formData.gratitudes[index]}
                  onChange={(e) => handleArrayInputChange(index, 'gratitudes', e.target.value)}
                  className="input input-bordered w-full"
                  placeholder={`Gratitude #${index + 1}`}
                />
              </div>
            ))}
          </div>
          
          {/* What did you Well? */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="well">
              What did you Well?
            </label>
            <textarea
              id="well"
              name="well"
              value={formData.well}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What did you do well today?"
            ></textarea>
          </div>
          
          {/* What could you do Better? */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="better">
              What could you do Better?
            </label>
            <textarea
              id="better"
              name="better"
              value={formData.better}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What could you improve on?"
            ></textarea>
          </div>
          
          {/* How could you do Better? */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="how">
              How could you do Better?
            </label>
            <textarea
              id="how"
              name="how"
              value={formData.how}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="How could you improve in the future?"
            ></textarea>
          </div>
          
          {/* Note Section */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="Any additional notes or thoughts..."
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Saving...
                </>
              ) : 'Save Journal Entry'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DailySuccessJournal;